import React, {useRef} from "react";
import {OrderEditorTabProps} from "@widgets/pos/order/OrderEditor";
import CardHeader from "peggirkit/dist/components/Card/CardHeading/CardHeader";
import {Alert, Card, SimpleCardHeading, Toggle, toggleDescriptionLabel} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {useFormContext, useWatch} from "react-hook-form";
import {PosOrderForm} from "@/data/entities/order";
import {
    emptyNewDeliveryAddress,
    formatAddress,
    OrderPlacementDeliveryAddress,
    OrderPlacementNewDeliveryAddress,
} from "@/data/entities/deliveryAddress";
import AddressFields from "@widgets/pos/order/customer/address/AddressFields";

type AddressOption = {
    key: string,
    title: React.ReactNode,
    description: React.ReactNode,
    value: () => OrderPlacementDeliveryAddress,
};

const optionKey = (address: OrderPlacementDeliveryAddress): string => {
    switch (address.type) {
        case "EXISTING":
            return `EXISTING-${address.id}`;
        default:
            return address.type;
    }
};

/**
 * Where to deliver: an address of the selected account, the business address
 * or a new address.
 */
const DeliveryAddressCard = ({isPending}: OrderEditorTabProps) => {
    const intl = useIntl();
    const {setValue, getValues} = useFormContext<PosOrderForm>();
    const deliveryAddress = useWatch<PosOrderForm, "deliveryAddress">({name: "deliveryAddress"});
    const customer = useWatch<PosOrderForm, "customer">({name: "customer"});
    const businessDetails = useWatch<PosOrderForm, "businessDetails">({name: "businessDetails"});

    // Keep a new address that was filled in when switching back and forth
    const lastNewAddress = useRef<OrderPlacementNewDeliveryAddress | null>(null);
    const selectAddress = (address: OrderPlacementDeliveryAddress) => {
        const current = getValues("deliveryAddress");
        if (current.type === "NEW")
            lastNewAddress.current = current;

        setValue("deliveryAddress", address, {shouldDirty: true});
    };

    const options: AddressOption[] = [
        ...(customer?.deliveryAddresses ?? []).map((address): AddressOption => ({
            key: `EXISTING-${address.id}`,
            title: address.name,
            description: formatAddress(address),
            value: () => ({type: "EXISTING", id: address.id}),
        })),
        ...(businessDetails !== null ? [{
            key: "BUSINESS",
            title: <FormattedMessage id={"businessAddress"}/>,
            description: formatAddress(businessDetails) || <FormattedMessage id={"businessAddressNotFilledIn"}/>,
            value: (): OrderPlacementDeliveryAddress => ({type: "BUSINESS"}),
        }] : []),
        {
            key: "NEW",
            title: <FormattedMessage id={"newAddress"}/>,
            description: <FormattedMessage id={"newAddressDescription"}/>,
            value: () => lastNewAddress.current ?? emptyNewDeliveryAddress(),
        },
    ];
    const selectedKey = optionKey(deliveryAddress);

    return (
        <Card
            header={
                <CardHeader>
                    <SimpleCardHeading title={<FormattedMessage id={"deliveryAddress"}/>}/>
                </CardHeader>
            }
        >
            <div className={"flex flex-col gap-6"}>
                {
                    // Only a choice when there is more than a new address
                    options.length > 1 &&
                    <fieldset className={"flex flex-col gap-4"}>
                        <legend className={"sr-only"}>{intl.formatMessage({id: "deliveryAddress"})}</legend>
                        {options.map(option => (
                            <Toggle
                                key={option.key}
                                type={"radio"}
                                name={"pos-delivery-address"}
                                value={option.key}
                                label={toggleDescriptionLabel(option.title, option.description)}
                                checked={selectedKey === option.key}
                                onChange={() => selectAddress(option.value())}
                                disabled={isPending}
                            />
                        ))}
                    </fieldset>
                }

                {
                    deliveryAddress.type === "BUSINESS" &&
                    <Alert
                        type={"primary"}
                        title={<FormattedMessage id={"deliverToBusinessAddress"}/>}
                        content={<FormattedMessage id={"deliverToBusinessAddressDescription"}/>}
                    />
                }

                {
                    deliveryAddress.type === "NEW" &&
                    <AddressFields
                        prefix={"deliveryAddress.address"}
                        required={true}
                        isPending={isPending}
                        showName={customer !== null}
                    />
                }
            </div>
        </Card>
    );
};

export default DeliveryAddressCard;